import React from 'react';
import {
  TerminalWindow,
  Stack,
  FolderOpen,
  SidebarSimple,
  CaretLeft,
  CaretRight,
  User
} from '@phosphor-icons/react';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';

const MainSidebar = ({
  activeSurface = 'workspace',
  onSelectSurface,
  onOpenProjects,
  isCollapsed = false,
  onToggleCollapse,
  user = null,
  className = ''
}) => {
  const navItems = [
    {
      id: 'workspace',
      label: 'Workspace',
      icon: TerminalWindow,
      onClick: () => {
        if (onSelectSurface) onSelectSurface('workspace');
      },
      isActive: activeSurface === 'workspace'
    },
    {
      id: 'layers',
      label: 'Capas',
      icon: Stack,
      badge: <OperationalStatusBadge status={OPERATIONAL_STATUS.STATIC} size="sm" />,
      onClick: () => {
        if (onSelectSurface) onSelectSurface('layers');
      },
      isActive: activeSurface === 'layers'
    },
    {
      id: 'projects',
      label: 'Proyectos',
      icon: FolderOpen,
      onClick: () => {
        if (onOpenProjects) onOpenProjects();
      },
      isActive: false
    }
  ];

  return (
    <aside
      data-testid="main-sidebar"
      aria-label="Navegación principal"
      className={`h-full border-r border-white/[0.08] bg-[#0E1117] flex flex-col justify-between select-none transition-all duration-200 shrink-0 ${
        isCollapsed ? 'w-14' : 'w-52'
      } ${className}`}
    >
      {/* Top section: Brand / Collapse Toggle + Nav */}
      <div className="flex flex-col min-h-0">
        {/* Sidebar Header with Toggle Control */}
        <div className="h-11 px-3 flex items-center justify-between border-b border-white/[0.06] shrink-0">
          {!isCollapsed && (
            <span className="font-mono text-xs font-bold tracking-wider text-slate-300 uppercase">
              VASTEON
            </span>
          )}
          <button
            type="button"
            data-testid="sidebar-toggle-btn"
            aria-label={isCollapsed ? 'Expandir navegación' : 'Colapsar navegación'}
            title={isCollapsed ? 'Expandir navegación' : 'Colapsar navegación'}
            onClick={onToggleCollapse}
            className={`p-1.5 hover:bg-white/[0.06] text-slate-400 hover:text-white rounded-md transition-colors ${
              isCollapsed ? 'mx-auto' : ''
            }`}
          >
            <SidebarSimple size={16} />
          </button>
        </div>

        {/* Primary Navigation Movements */}
        <nav className="p-2 space-y-1" aria-label="Espacios principales">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                data-testid={`nav-item-${item.id}`}
                aria-label={item.label}
                aria-current={item.isActive ? 'page' : undefined}
                title={item.label}
                onClick={item.onClick}
                className={`w-full flex items-center rounded-md font-sans text-xs font-medium transition-all ${
                  isCollapsed
                    ? 'justify-center p-2.5'
                    : 'justify-between px-3 py-2'
                } ${
                  item.isActive
                    ? 'bg-white/[0.08] text-cyan-300 shadow-xs'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Icon
                    size={16}
                    className={`shrink-0 ${
                      item.isActive ? 'text-cyan-400' : 'text-slate-400'
                    }`}
                  />
                  {!isCollapsed && (
                    <span className="truncate">{item.label}</span>
                  )}
                </div>
                {!isCollapsed && item.badge && (
                  <div className="shrink-0 ml-1">{item.badge}</div>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Bottom section: Reserved User Identity Context */}
      <div className="p-2 border-t border-white/[0.06] shrink-0">
        <div
          data-testid="sidebar-user-context"
          className={`flex items-center rounded-md text-slate-400 text-xs ${
            isCollapsed ? 'justify-center p-2' : 'gap-2 px-2.5 py-1.5'
          }`}
          title={user?.display_name || user?.email || 'Usuario'}
        >
          <div className="w-6 h-6 rounded-full bg-white/[0.05] border border-white/[0.1] flex items-center justify-center text-slate-300 shrink-0">
            <User size={13} />
          </div>
          {!isCollapsed && (
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-slate-300 truncate text-[11px]">
                {user?.display_name || 'Arquitecto'}
              </span>
              <span className="text-[10px] text-slate-500 truncate">
                {user?.email || 'Sesión local'}
              </span>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default MainSidebar;
